import { useState } from "react";
import {
  selectIsAllowedToPublish,
  selectIsLocalVideoPluginPresent,
  selectLocalVideoTrackID,
  useAVToggle,
  useHMSActions,
  useHMSStore,
} from "@100mslive/react-sdk";
import { Mic, MicOff, Sparkles, Video, VideoOff } from "lucide-react";
import { toast } from "sonner";
import { ControlButton } from "@/components/common/ControlButton";
import { getBlurPluginName, setBlur } from "@/lib/virtualBackground";

const isMac = () => /Mac|iPhone|iPad/.test(navigator.platform || "");

const shortcut = key => `${isMac() ? "⌘" : "Ctrl"}+${key}`;

export const MicToggle = () => {
  const { isLocalAudioEnabled, toggleAudio } = useAVToggle();
  if (!toggleAudio) {
    return null;
  }
  return (
    <ControlButton
      data-testid="audio_btn"
      label={`${isLocalAudioEnabled ? "Mute" : "Unmute"} microphone (${shortcut("D")})`}
      caption={isLocalAudioEnabled ? "Mute" : "Unmute"}
      off={!isLocalAudioEnabled}
      onClick={() => toggleAudio()}
    >
      {isLocalAudioEnabled ? <Mic /> : <MicOff data-testid="audio_off_btn" />}
    </ControlButton>
  );
};

export const CameraToggle = () => {
  const { isLocalVideoEnabled, toggleVideo } = useAVToggle();
  if (!toggleVideo) {
    return null;
  }
  return (
    <ControlButton
      data-testid="video_btn"
      label={`${isLocalVideoEnabled ? "Stop" : "Start"} video (${shortcut("E")})`}
      caption={isLocalVideoEnabled ? "Stop video" : "Start video"}
      off={!isLocalVideoEnabled}
      onClick={() => toggleVideo()}
    >
      {isLocalVideoEnabled ? (
        <Video />
      ) : (
        <VideoOff data-testid="video_off_btn" />
      )}
    </ControlButton>
  );
};

export const BlurToggle = () => {
  const hmsActions = useHMSActions();
  const allowedToPublish = useHMSStore(selectIsAllowedToPublish);
  const videoTrackId = useHMSStore(selectLocalVideoTrackID);
  const isBlurOn = useHMSStore(
    selectIsLocalVideoPluginPresent(getBlurPluginName() || "")
  );
  const [busy, setBusy] = useState(false);

  if (!allowedToPublish?.video || !videoTrackId) {
    return null;
  }

  const toggle = async () => {
    setBusy(true);
    try {
      await setBlur(hmsActions, !isBlurOn);
    } catch (error) {
      toast.error("Background blur isn't available", {
        description: error?.message,
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <ControlButton
      data-testid="virtual_bg_btn"
      label={`Turn background blur ${isBlurOn ? "off" : "on"}`}
      caption="Blur"
      active={Boolean(isBlurOn)}
      disabled={busy}
      onClick={toggle}
    >
      <Sparkles />
    </ControlButton>
  );
};
